import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { AvailabilityLabel } from '../lib/labels'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'
import PeopleTabs from '../components/PeopleTabs'
import { downloadCsv } from '../utils/csv'

// 원수사 디렉터리 페이지
// - 담당자 검색/정렬/즐겨찾기/메모/의뢰 시작 등 상호작용 제공
// - 상태 라벨은 AvailabilityLabel을 사용해 일관된 한국어 표기
export default function CounterpartDirectoryPage() {
    const { show } = useToast()
    const navigate = useNavigate()
    const { user } = useAuth() || { user: null }
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [savingAssignment, setSavingAssignment] = useState(new Set())
    const [savingMode, setSavingMode] = useState(false)
    const [createInsurerUserOpen, setCreateInsurerUserOpen] = useState(false)
    const [createInsurerUserForm, setCreateInsurerUserForm] = useState({
        name: '',
        email: '', // (선택) 직접 지정 시 /users 사용, 비우면 더미 이메일 자동 생성
        phoneNumber: '',
        role: 'INSURER_MANAGER',
        password: '',
        gaBranchOrgId: '', // (GA_PROXY 생성 시) 배정할 GA 지점/지사
    })
    const [gaBranchOptions, setGaBranchOptions] = useState([]) // [{id,name,type,parentOrgId}]

    // 검색 및 필터 상태
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('ALL')
    const [orgFilter, setOrgFilter] = useState('ALL') // 원수사 유저용 GA 필터
    const [kpiFilter] = useState('ALL')
    const [sortBy, setSortBy] = useState('responseTime')

    // 데이터 상태
    const [counterparts, setCounterparts] = useState([])
    const [orgOptions, setOrgOptions] = useState([]) // GA: 매핑된 원수사, 원수사: 매핑된 GA
    const [favorites, setFavorites] = useState(new Set())
    const [userNotes, setUserNotes] = useState({})
    const [noteDraft, setNoteDraft] = useState('')

    // 선택된 담당자 상세 정보
    const [selectedCounterpart, setSelectedCounterpart] = useState(null)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [detailEditMode, setDetailEditMode] = useState(false)
    const [detailSaving, setDetailSaving] = useState(false)
    const [detailEditForm, setDetailEditForm] = useState({ name: '', phoneNumber: '', gaBranchOrgId: '' })
    const [selectedGaBranches, setSelectedGaBranches] = useState([]) // [{gaBranchOrgId, gaBranchOrgName, ...}]
    const [loadingSelectedGaBranches, setLoadingSelectedGaBranches] = useState(false)

    // 원수사(회사)별 "담당자 배정 여부" 캐시
    const [assignedByCompanyId, setAssignedByCompanyId] = useState({}) // { [companyOrgId: string]: boolean }
    const [checkingAssignedCompanyIds, setCheckingAssignedCompanyIds] = useState(new Set()) // Set<string>

    const debouncedSearch = useDebouncedValue(searchQuery, 300)

    const roles = Array.isArray(user?.roles) ? user.roles : []
    const isGaUser = roles.some(r => r.startsWith('GA_'))
    const isInsurerUser = roles.some(r => r.startsWith('INSURER_'))
    const canManageAssignments = roles.includes('OPERATOR') || roles.includes('GA_LEADER') || roles.includes('GA_BRANCH_MANAGER') || roles.includes('GA_DEPUTY_BRANCH_MANAGER') || roles.includes('GA_MASTER_ADMIN') || roles.includes('GA_MASTER_DEPUTY_ADMIN')
    // 정책:
    // - GA(설계사 포함)는 "원수사 담당자" 상세 팝업을 볼 수 있다(수정은 불가).
    // - INSURER 사용자는 "GA 사용자(우리 조직 사용자)" 상세는 노출하지 않는다. (필요 시 OPERATOR만 예외)
    const isOperator = roles.includes('OPERATOR')
    const canViewCounterpartDetail = isGaUser || isOperator

    const canEditGaProxyCounterpart = useMemo(() => {
        if (!isGaUser) return false
        if (!canManageAssignments) return false
        if (!selectedCounterpart) return false
        return selectedCounterpart.createdSource === 'GA_PROXY'
    }, [isGaUser, canManageAssignments, selectedCounterpart])

    const reloadSelectedGaBranches = useCallback(async () => {
        if (!selectedCounterpart?.id) return
        try {
            const res = await api.get(`/users/${selectedCounterpart.id}/ga-branches`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const list = Array.isArray(payload) ? payload : []
            setSelectedGaBranches(list)
        } catch {
            setSelectedGaBranches([])
        }
    }, [selectedCounterpart])

    const addGaBranchMappingForSelected = useCallback(async () => {
        if (!selectedCounterpart?.id) return
        const desired = detailEditForm.gaBranchOrgId ? Number(detailEditForm.gaBranchOrgId) : null
        if (!desired) return show('추가할 담당 지사를 선택해주세요', 'error')
        setDetailSaving(true)
        try {
            await api.post(`/users/${selectedCounterpart.id}/ga-branches`, { gaBranchOrgId: desired })
            await reloadSelectedGaBranches()
            // reload는 파일 아래쪽에서 선언되는 useCallback이라 TDZ 문제가 날 수 있어,
            // 직접 호출 대신 동일한 요청을 수행하는 effect에 맡긴다.
            show('담당 지사가 추가되었습니다', 'success')
        } catch (err) {
            const msg =
                err?.response?.data?.message
                || err?.response?.data?.msg
                || '담당 지사 추가에 실패했습니다. (권한/조직 범위를 확인해주세요)'
            show(msg, 'error')
        } finally {
            setDetailSaving(false)
        }
    }, [detailEditForm.gaBranchOrgId, reloadSelectedGaBranches, selectedCounterpart, show])

    const removeGaBranchMappingForSelected = useCallback(async (gaBranchOrgId, gaBranchOrgName) => {
        if (!selectedCounterpart?.id) return
        const label = (gaBranchOrgName || '').toString().trim() || '해당 조직/지사'
        const ok = window.confirm(`${label}에서 생성 및 배정한 매니저입니다. 삭제하시겠습니까?`)
        if (!ok) return
        setDetailSaving(true)
        try {
            const res = await api.delete(`/users/${selectedCounterpart.id}/ga-branches/${Number(gaBranchOrgId)}`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            if (payload && payload.deleted === false) {
                show('이미 배정 해제된 상태입니다', 'success')
            } else {
                show('배정이 해제되었습니다', 'success')
            }
            await reloadSelectedGaBranches()
        } catch (err) {
            const msg =
                err?.response?.data?.message
                || err?.response?.data?.msg
                || '배정 해제에 실패했습니다.'
            show(msg, 'error')
        } finally {
            setDetailSaving(false)
        }
    }, [reloadSelectedGaBranches, selectedCounterpart, show])

    // 상세 모달 오픈 시: GA_PROXY 담당 지사 매핑 로딩
    useEffect(() => {
        let ignore = false
        if (!showDetailModal || !selectedCounterpart?.id) return () => { ignore = true }
        if (!canEditGaProxyCounterpart) return () => { ignore = true }
        ; (async () => {
            if (!ignore) setLoadingSelectedGaBranches(true)
            try {
                const res = await api.get(`/users/${selectedCounterpart.id}/ga-branches`)
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                const list = Array.isArray(payload) ? payload : []
                if (!ignore) {
                    setSelectedGaBranches(list)
                    // 편집 중에 서버값이 덮어쓰지 않도록, 기본값 세팅은 편집 모드가 아닐 때만
                    if (!detailEditMode) {
                        const primary = list[0]
                        if (primary?.gaBranchOrgId != null) {
                            setDetailEditForm(prev => ({ ...prev, gaBranchOrgId: String(primary.gaBranchOrgId) }))
                        }
                    }
                }
            } catch {
                if (!ignore) setSelectedGaBranches([])
            } finally {
                if (!ignore) setLoadingSelectedGaBranches(false)
            }
        })()
        return () => { ignore = true }
    }, [showDetailModal, selectedCounterpart, canEditGaProxyCounterpart, detailEditMode])

    // GA: 보험 유형(생명/손해) 및 선택된 원수사
    const [insurerCategory, setInsurerCategory] = useState('NONLIFE') // ALL | LIFE | NONLIFE, 기본 손해보험
    const [selectedInsurerId, setSelectedInsurerId] = useState(null)
    const [gaViewMode, setGaViewMode] = useState('CARDS') // CARDS | ALL_LIST

    const ensureAssignedForCompany = useCallback(async (companyOrgId) => {
        const id = String(companyOrgId)
        if (Object.prototype.hasOwnProperty.call(assignedByCompanyId, id)) {
            return !!assignedByCompanyId[id]
        }
        if (checkingAssignedCompanyIds.has(id)) {
            return null
        }
        setCheckingAssignedCompanyIds(prev => new Set([...prev, id]))
        try {
            const res = await api.get('/counterparts/assignment-status', { params: { orgIds: id } })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const list = Array.isArray(payload) ? payload : []
            const row = list.find(r => String(r?.orgId) === id) || null
            const ok = !!row?.assigned
            setAssignedByCompanyId(prev => ({ ...prev, [id]: ok }))
            return ok
        } catch {
            setAssignedByCompanyId(prev => ({ ...prev, [id]: false }))
            return false
        } finally {
            setCheckingAssignedCompanyIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [assignedByCompanyId, checkingAssignedCompanyIds])

    const ensureAssignedBatch = useCallback(async (companyOrgIds) => {
        const ids = Array.isArray(companyOrgIds) ? companyOrgIds.map(x => String(x)).filter(Boolean) : []
        if (ids.length === 0) return
        const missing = ids.filter(id => !Object.prototype.hasOwnProperty.call(assignedByCompanyId, id))
        if (missing.length === 0) return
        try {
            const res = await api.get('/counterparts/assignment-status', { params: { orgIds: missing.join(',') } })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const list = Array.isArray(payload) ? payload : []
            const next = {}
            list.forEach(it => {
                const key = it?.orgId != null ? String(it.orgId) : null
                if (!key) return
                next[key] = !!it.assigned
            })
            if (Object.keys(next).length > 0) {
                setAssignedByCompanyId(prev => ({ ...prev, ...next }))
            }
        } catch {
            // ignore
        }
    }, [assignedByCompanyId])

    const reload = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const params = {
                q: debouncedSearch || undefined,
                status: statusFilter !== 'ALL' ? statusFilter : undefined,
                kpiRange: kpiFilter !== 'ALL' ? kpiFilter : undefined,
                sortBy
            }

            // GA: 카드 뷰에서는 선택된 원수사만, 전체 목록 뷰에서는 전체 원수사(=orgId 필터 없음)
            if (isGaUser && selectedInsurerId && gaViewMode !== 'ALL_LIST') {
                params.orgId = selectedInsurerId
            } else if (isInsurerUser && orgFilter !== 'ALL') {
                params.orgId = orgFilter
            }

            const [counterpartsRes, favoritesRes, notesRes] = await Promise.all([
                api.get('/counterparts', { params }),
                api.get('/counterparts/favorites'),
                api.get('/counterparts/notes')
            ])

            const cps = Array.isArray(counterpartsRes.data?.data) ? counterpartsRes.data.data : (counterpartsRes.data || [])
            const favs = (favoritesRes.data?.data ?? favoritesRes.data) || []
            const notes = (notesRes.data?.data ?? notesRes.data) || {}

            setCounterparts(cps)
            setFavorites(new Set(favs))
            setUserNotes(notes)
        } catch (err) {
            setError('담당자 정보를 불러오지 못했습니다')
        } finally {
            setLoading(false)
        }
    }, [debouncedSearch, statusFilter, orgFilter, kpiFilter, sortBy, isGaUser, isInsurerUser, selectedInsurerId, gaViewMode])

    // 데이터 로딩
    useEffect(() => {
        let ignore = false
            ; (async () => {
                if (ignore) return
                await reload()
            })()
        return () => { ignore = true }
    }, [reload])

    // NOTE: 탭 배지(배정/미배정 카운트)는 제거하고, 매핑 여부만 표시한다.

    // 조직 선택 옵션: 로그인한 지점에 매핑된 원수사/GA 조직
    useEffect(() => {
        let ignore = false
        async function loadOrgOptions() {
            try {
                if (!user?.orgId) {
                    if (!ignore) setOrgOptions([])
                    return
                }
                const res = await api.get(`/orgs/${user.orgId}`)
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data

                let options = []
                if (isGaUser) {
                    options = Array.isArray(payload?.mappedInsurers) ? payload.mappedInsurers : []
                } else if (isInsurerUser) {
                    options = Array.isArray(payload?.mappedGAs) ? payload.mappedGAs : []
                } else {
                    options = []
                }

                if (!ignore) setOrgOptions(options)
            } catch {
                if (!ignore) setOrgOptions([])
            }
        }
        loadOrgOptions()
        return () => { ignore = true }
    }, [user, isGaUser, isInsurerUser])

    // NOTE: selectedInsurerId는 "보험사 회사 노드" id이며, 실제 매핑은 회사 하위의 특정 조직(지점/운영단위)로 존재할 수 있다.

    const generatePassword = React.useCallback(() => {
        // 12자리 정도의 간단한 임시 비밀번호(공유용)
        const s = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
        return s.slice(0, 12)
    }, [])

    useEffect(() => {
        if (!createInsurerUserOpen) return
        setCreateInsurerUserForm(prev => ({ ...prev, password: prev.password || generatePassword() }))
    }, [createInsurerUserOpen, generatePassword])

    // "배정할 GA 지점/지사" 옵션 로딩 (생성 모달/상세 수정 모달 공용)
    useEffect(() => {
        let ignore = false
        if (!isGaUser) return () => { ignore = true }
        if (!user?.orgId) return () => { ignore = true }
        ; (async () => {
            try {
                const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                const list = Array.isArray(payload) ? payload : []
                const gaBranches = list
                    .filter(o => o && String(o.type).toUpperCase() === 'GA')
                    .filter(o => o.parentOrgId != null) // GA 루트 제외
                if (!ignore) setGaBranchOptions(gaBranches)
            } catch {
                if (!ignore) setGaBranchOptions([])
            }
        })()
        return () => { ignore = true }
    }, [isGaUser, user?.orgId])

    // 생성 모달이 열릴 때만: 기본 배정 지사 설정
    useEffect(() => {
        if (!createInsurerUserOpen) return
        if (!isGaUser) return
        if (!user?.orgId) return
        setCreateInsurerUserForm(prev => ({
            ...prev,
            gaBranchOrgId: prev.gaBranchOrgId || String(user.orgId),
        }))
    }, [createInsurerUserOpen, isGaUser, user])

    const refreshOrgOptions = useCallback(async () => {
        try {
            if (!user?.orgId) return
            const res = await api.get(`/orgs/${user.orgId}`)
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            if (isGaUser) {
                setOrgOptions(Array.isArray(payload?.mappedInsurers) ? payload.mappedInsurers : [])
            } else if (isInsurerUser) {
                setOrgOptions(Array.isArray(payload?.mappedGAs) ? payload.mappedGAs : [])
            }
        } catch {
            // ignore
        }
    }, [user, isGaUser, isInsurerUser])

    const updateManagerAssignmentMode = async (mode, insurerOrgIdForMapping) => {
        if (!isGaUser || !user?.orgId || !insurerOrgIdForMapping) return
        if (!canManageAssignments) return show('권한이 없습니다', 'error')
        try {
            setSavingMode(true)
            await api.patch(`/orgs/${user.orgId}/mappings/manager-assignment-mode`, { counterpartOrgId: Number(insurerOrgIdForMapping), mode })
            await refreshOrgOptions()
            await reload()
            show('설정이 저장되었습니다', 'success')
        } catch (err) {
            show('설정 저장 실패', 'error')
        } finally {
            setSavingMode(false)
        }
    }

    const toggleGaAssignment = async (counterpart) => {
        if (!isGaUser || !user?.orgId) return
        if (!counterpart?.assignmentEditable) return show('권한이 없습니다', 'error')
        const key = counterpart.id
        setSavingAssignment(prev => new Set([...prev, key]))
        try {
            if (counterpart.gaAssigned) {
                // 상위 조직(사업단)에서 "배정해제"를 누르는 경우,
                // 실제 매핑은 하위 지사에 걸려 있어 (userId, 사업단Id) 조합의 매핑 row가 없을 수 있다.
                // 이 경우 "어느 조직/지사에서 생성·배정된 담당자인지"를 확인 문구로 안내한다.
                try {
                    const res = await api.get(`/users/${counterpart.id}/ga-branches`)
                    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                    const list = Array.isArray(payload) ? payload : []
                    const hasExact = list.some(m => String(m?.gaBranchOrgId) === String(user.orgId))
                    if (!hasExact && list.length > 0) {
                        const names = list
                            .map(m => (m?.gaBranchOrgName || '').toString().trim())
                            .filter(Boolean)
                        const originLabel = names.length > 0 ? names.join(', ') : '하위 조직/지사'
                        const ok = window.confirm(`${originLabel}에서 생성 및 배정한 매니저입니다. 삭제하시겠습니까?`)
                        if (!ok) return
                    }
                } catch {
                    // 매핑 조회 실패 시에는 기존 동작(삭제 시도) 유지
                }

                const del = await api.delete(`/users/${counterpart.id}/ga-branches/${user.orgId}`)
                const delPayload = (del?.data && del.data.data !== undefined) ? del.data.data : del.data
                if (delPayload && delPayload.deleted === false) {
                    show('이미 배정 해제된 상태입니다', 'success')
                } else {
                    show('배정이 해제되었습니다', 'success')
                }
            } else {
                await api.post(`/users/${counterpart.id}/ga-branches`, { gaBranchOrgId: user.orgId })
                show('담당자로 배정되었습니다', 'success')
            }
            await reload()
        } catch (err) {
            show('처리 실패', 'error')
        } finally {
            setSavingAssignment(prev => {
                const next = new Set(prev)
                next.delete(key)
                return next
            })
        }
    }

    const createInsurerUser = async (e) => {
        e.preventDefault()
        if (!isGaUser || !user?.orgId || !selectedInsurerId) return
        if (!canManageAssignments) return show('권한이 없습니다', 'error')
        try {
            // 생성은 "회사 노드"가 아니라, 실제 매핑된 원수사 지점/운영단위에 귀속되어야
            // /counterparts 조회 범위(매핑 기반)에도 포함되어 생성 직후 바로 노출된다.
            const effectiveInsurerOrgId = Number(selectedInsurerMappingOrgId || selectedInsurerId)
            const role = createInsurerUserForm.role
            const hasEmail = Boolean(createInsurerUserForm.email && String(createInsurerUserForm.email).trim())
            if (hasEmail) {
                const roles = [role]
                await api.post('/users', {
                    name: createInsurerUserForm.name,
                    email: String(createInsurerUserForm.email).trim(),
                    password: createInsurerUserForm.password,
                    orgId: effectiveInsurerOrgId,
                    roles,
                    phoneNumber: createInsurerUserForm.phoneNumber,
                })
            } else {
                await api.post('/users/proxy-insurer-managers', {
                    insurerOrgId: effectiveInsurerOrgId,
                    name: createInsurerUserForm.name,
                    phoneNumber: createInsurerUserForm.phoneNumber,
                    role,
                    gaBranchOrgId: createInsurerUserForm.gaBranchOrgId ? Number(createInsurerUserForm.gaBranchOrgId) : undefined,
                })
            }
            show('원수사 담당자가 생성되었습니다', 'success')
            setCreateInsurerUserOpen(false)
            setCreateInsurerUserForm({ name: '', email: '', phoneNumber: '', role: 'INSURER_MANAGER', password: '', gaBranchOrgId: '' })
            await reload()
        } catch (err) {
            show('생성 실패', 'error')
        }
    }

    // 즐겨찾기 토글
    const toggleFavorite = async (counterpartId) => {
        try {
            const isFavorited = favorites.has(counterpartId)
            if (isFavorited) {
                await api.delete(`/counterparts/${counterpartId}/favorite`)
                setFavorites(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(counterpartId)
                    return newSet
                })
                show('즐겨찾기에서 제거되었습니다', 'success')
            } else {
                await api.post(`/counterparts/${counterpartId}/favorite`)
                setFavorites(prev => new Set([...prev, counterpartId]))
                show('즐겨찾기에 추가되었습니다', 'success')
            }
        } catch (err) {
            show('즐겨찾기 처리 실패', 'error')
        }
    }

    // 메모 저장
    const saveNote = async (counterpartId, note) => {
        try {
            await api.post(`/counterparts/notes/${counterpartId}`, { note })
            setUserNotes(prev => ({ ...prev, [counterpartId]: note }))
            show('메모가 저장되었습니다', 'success')
        } catch (err) {
            show('메모 저장 실패', 'error')
        }
    }

    // NOTE(정책): 설계 의뢰 생성 채널은 "설계관리" 메뉴로 일원화.
    // - 따라서 원수사/담당자 탭에서는 설계의뢰 진입 버튼을 숨긴다.
    // const startRequest = (counterpart) => { ... }

    // NOTE: 공지 보기 기능은 UX 확정 시 활성화(현재 미사용)

    // 상태별 스타일
    const getStatusStyle = (status) => {
        switch (status) {
            case 'ON_DUTY':
                return 'bg-green-100 text-green-800'
            case 'TRAINING':
                return 'bg-blue-100 text-blue-800'
            case 'VACATION':
                return 'bg-yellow-100 text-yellow-800'
            case 'AWAY':
                return 'bg-red-100 text-red-800'
            default:
                return 'bg-gray-100 text-gray-800'
        }
    }

    // 정렬된 데이터
    const sortedCounterparts = useMemo(() => {
        const filtered = counterparts.filter(cp => {
            // 즐겨찾기 우선 표시 옵션 (필터에서 제어)
            return true
        })

        return filtered.sort((a, b) => {
            switch (sortBy) {
                case 'responseTime':
                    return (a.avgResponseTime || 0) - (b.avgResponseTime || 0)
                case 'recentActivity':
                    return new Date(b.lastActivityAt || 0) - new Date(a.lastActivityAt || 0)
                case 'completionCount':
                    return (b.completionCount || 0) - (a.completionCount || 0)
                case 'name':
                    return a.name.localeCompare(b.name)
                default:
                    return 0
            }
        })
    }, [counterparts, sortBy])

    const secondLabel = isGaUser ? '원수사 담당자' : (isInsurerUser ? 'GA 사용자' : '담당자')
    const subtitle = isGaUser
        ? '우리 GA에 매핑된 원수사 담당자 목록'
        : (isInsurerUser ? '우리 조직이 담당하는 GA 사용자 목록' : '담당자 목록')

    // 모든 조직 정보를 한 번 로딩(회사명 계산용)
    const [allOrgs, setAllOrgs] = useState([])

    useEffect(() => {
        let ignore = false
        async function loadAllOrgs() {
            try {
                const res = await api.get('/orgs/options', { params: { scope: 'all' } })
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (!ignore) setAllOrgs(Array.isArray(payload) ? payload : [])
            } catch {
                if (!ignore) setAllOrgs([])
            }
        }
        loadAllOrgs()
        return () => { ignore = true }
    }, [])

    const orgById = useMemo(() => {
        const map = {}
        allOrgs.forEach(o => {
            if (o && o.id != null) map[o.id] = o
        })
        return map
    }, [allOrgs])

    const isInsurerCompanyNode = useCallback((org) => {
        if (!org) return false
        if (org.type !== 'INSURER') return false
        const p = org.parentOrgId ? orgById[org.parentOrgId] : null
        if (!p) return false
        const gp = p.parentOrgId ? orgById[p.parentOrgId] : null
        return !gp
    }, [orgById])

    const insurerCompanies = useMemo(() => {
        return allOrgs
            .filter(o => o && o.type === 'INSURER')
            .filter(o => isInsurerCompanyNode(o))
    }, [allOrgs, isInsurerCompanyNode])

    const resolveCompanyId = useCallback((orgId) => {
        const visited = new Set()
        let cur = orgById[orgId]
        let guard = 0
        while (cur && !visited.has(cur.id) && guard++ < 50) {
            visited.add(cur.id)
            if (isInsurerCompanyNode(cur)) return cur.id
            cur = cur.parentOrgId ? orgById[cur.parentOrgId] : null
        }
        return orgId
    }, [orgById, isInsurerCompanyNode])

    const mappedCompanyIds = useMemo(() => {
        // orgOptions = 내 GA에 매핑된 원수사(조직) 목록. 이를 보험사 "회사" 노드 기준으로 정규화한다.
        const s = new Set()
        for (const o of orgOptions) {
            if (!o?.id) continue
            s.add(resolveCompanyId(o.id))
        }
        return s
    }, [orgOptions, resolveCompanyId])

    // 선택된 보험사(회사) 탭에 대응하는 "실제 매핑된 원수사 조직(지점/운영단위)"을 찾는다.
    // - 모드 전환은 insurer_ga_mappings(ga_org_id, insurer_org_id) 행을 찾아 업데이트해야 하므로 회사 id로는 안 되고,
    //   회사 하위에서 실제로 매핑되어 있는 orgId를 사용해야 한다.
    const selectedMappedInsurerOrg = useMemo(() => {
        if (!isGaUser || !selectedInsurerId) return null
        // orgOptions는 "내 GA에 매핑된 insurer org" 목록
        return orgOptions.find(o => o?.id && resolveCompanyId(o.id) === selectedInsurerId) || null
    }, [isGaUser, selectedInsurerId, orgOptions, resolveCompanyId])

    const selectedManagerAssignmentMode = String(selectedMappedInsurerOrg?.managerAssignmentMode || 'INSURER_MANAGED')
    const selectedInsurerMappingOrgId = selectedMappedInsurerOrg?.id || null

    const getInsurerDisplayName = (orgId, fallbackName) => {
        const visited = new Set()
        let cur = orgById[orgId]
        const insurers = []
        while (cur && !visited.has(cur.id)) {
            visited.add(cur.id)
            if (cur.type === 'INSURER') {
                insurers.push(cur)
            }
            cur = cur.parentOrgId ? orgById[cur.parentOrgId] : null
        }
        if (insurers.length > 0) {
            // 맨 위 루트(예: '손해보험', '생명보험')가 아니라 실제 회사명(삼성화재 등)을 우선 사용
            const companies = insurers.filter(o => o.name !== '손해보험' && o.name !== '생명보험')
            const target = (companies.length > 0 ? companies[companies.length - 1] : insurers[insurers.length - 1])
            return target?.name || fallbackName || ''
        }
        return fallbackName || ''
    }

    const getInsurerRootCategory = useCallback((orgId) => {
        const visited = new Set()
        let cur = orgById[orgId]
        while (cur && !visited.has(cur.id)) {
            visited.add(cur.id)
            // 보험사 카테고리 루트(손해보험/생명보험)를 기준으로 분류
            if (cur.type === 'INSURER') {
                if (cur.name === '손해보험') return 'NONLIFE'
                if (cur.name === '생명보험') return 'LIFE'
            }
            cur = cur.parentOrgId ? orgById[cur.parentOrgId] : null
        }
        return null
    }, [orgById])

    const formatDateYmd = () => {
        const d = new Date()
        const yyyy = String(d.getFullYear())
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${yyyy}${mm}${dd}`
    }

    // GA: 보험사(회사) 목록을 생명/손해로 분리 (매핑 여부와 무관)
    const lifeInsurers = useMemo(
        () => insurerCompanies.filter(o => getInsurerRootCategory(o.id) === 'LIFE'),
        [insurerCompanies, getInsurerRootCategory]
    )
    const nonLifeInsurers = useMemo(
        () => insurerCompanies.filter(o => getInsurerRootCategory(o.id) === 'NONLIFE'),
        [insurerCompanies, getInsurerRootCategory]
    )

    // 카테고리/선택된 원수사 초기화
    useEffect(() => {
        if (!isGaUser) return
        const currentList = insurerCategory === 'LIFE'
            ? lifeInsurers
            : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
        if (currentList.length === 0) {
            setSelectedInsurerId(null)
            return
        }
        // 선택된 원수사가 현재 카테고리에 없으면 첫번째로 교체
        if (!selectedInsurerId || !currentList.some(o => o.id === selectedInsurerId)) {
            setSelectedInsurerId(currentList[0].id)
        }
    }, [isGaUser, insurerCategory, lifeInsurers, nonLifeInsurers, insurerCompanies, selectedInsurerId])

    // GA: 탭에 "미배정" 표기를 위해 배정 여부를 백그라운드로 확인(캐시)
    useEffect(() => {
        if (!isGaUser) return
        const currentList = insurerCategory === 'LIFE'
            ? lifeInsurers
            : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
        let ignore = false
        ; (async () => {
            const ids = currentList.map(o => (o?.id != null ? String(o.id) : null)).filter(Boolean)
            const missing = ids.filter(id => !Object.prototype.hasOwnProperty.call(assignedByCompanyId, id))
            const slice = missing.slice(0, 60)
            if (ignore) return
            await ensureAssignedBatch(slice)
        })()
        return () => { ignore = true }
    }, [isGaUser, insurerCategory, lifeInsurers, nonLifeInsurers, insurerCompanies, assignedByCompanyId, ensureAssignedBatch])

    // 선택된 원수사에 따른 담당자 필터
    const visibleCounterparts = useMemo(() => {
        if (isGaUser) {
            if (gaViewMode === 'ALL_LIST') return sortedCounterparts
            if (!selectedInsurerId) return []
            const isUnderCompany = (orgId, companyId) => {
                const visited = new Set()
                let cur = orgById[orgId]
                let guard = 0
                while (cur && !visited.has(cur.id) && guard++ < 50) {
                    visited.add(cur.id)
                    if (cur.id === companyId) return true
                    cur = cur.parentOrgId ? orgById[cur.parentOrgId] : null
                }
                return false
            }
            return sortedCounterparts.filter(cp => cp?.orgId && isUnderCompany(cp.orgId, selectedInsurerId))
        }
        if (isInsurerUser && orgFilter !== 'ALL') {
            const orgIdNum = Number(orgFilter)
            return sortedCounterparts.filter(cp => cp.orgId === orgIdNum)
        }
        return sortedCounterparts
    }, [isGaUser, isInsurerUser, selectedInsurerId, orgFilter, sortedCounterparts, gaViewMode, orgById])

    // NOTE: activeTabOrgId는 현재 UI에서는 사용하지 않음(필요 시 부활)

    const cards = (
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleCounterparts.map(counterpart => (
                <div key={counterpart.id} className="rounded border p-4 hover:shadow-md transition-shadow">
                    {/* 헤더 */}
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <h3 className="font-semibold text-lg">{counterpart.name}</h3>
                            <p className="text-sm text-slate-600">{counterpart.position}</p>
                            <p className="text-xs text-slate-500">
                                {(() => {
                                    const company = getInsurerDisplayName(counterpart.orgId, counterpart.orgName)
                                    const orgName = counterpart.orgName || ''
                                    if (!company) return orgName || '-'
                                    if (!orgName) return company
                                    // 회사명이 이미 소속명에 포함되면 중복 표기 방지
                                    if (String(orgName).includes(String(company))) return orgName
                                    return `${company} · ${orgName}`
                                })()}
                            </p>
                            <p className="text-xs text-slate-500">
                                {(() => {
                                    const names = Array.isArray(counterpart?.gaBranchOrgNames)
                                        ? counterpart.gaBranchOrgNames.filter(Boolean)
                                        : []
                                    const label = names.length > 0 ? names.join(', ') : (counterpart?.gaBranchOrgName || '')
                                    return label ? `담당 지사: ${label}` : '담당 지사: -'
                                })()}
                            </p>
                            {counterpart.createdSource === 'GA_PROXY' && (
                                <div className="mt-1">
                                    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px]">
                                        GA 대리 생성
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                className={`p-1 rounded ${favorites.has(counterpart.id) ? 'text-yellow-500' : 'text-gray-400'}`}
                                onClick={() => toggleFavorite(counterpart.id)}
                                title="즐겨찾기"
                            >
                                {favorites.has(counterpart.id) ? '★' : '☆'}
                            </button>
                            {/* 배정/해제 버튼은 카드 하단으로 이동(상세정보 옆) */}
                        </div>
                    </div>

                    {/* 상태 */}
                    <div className="mb-3">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(counterpart.status)}`}>{AvailabilityLabel[counterpart.status] || counterpart.status}</span>
                        {counterpart.statusPeriod && (
                            <span className="text-xs text-slate-500 ml-2">{counterpart.statusPeriod}</span>
                        )}
                    </div>

                    {/* KPI */}
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div>
                            <span className="text-slate-600">평균 응답</span>
                            <div className="font-medium">{counterpart.avgResponseTime || '—'}시간</div>
                        </div>
                        <div>
                            <span className="text-slate-600">완료 리드타임</span>
                            <div className="font-medium">{counterpart.avgLeadTime || '—'}일</div>
                        </div>
                        <div>
                            <span className="text-slate-600">처리 건수</span>
                            <div className="font-medium">{counterpart.completionCount || 0}건</div>
                        </div>
                        <div>
                            <span className="text-slate-600">보완 요청률</span>
                            <div className="font-medium">{counterpart.revisionRate || '—'}%</div>
                        </div>
                    </div>

                    {/* 연락 정보 */}
                    <div className="text-xs text-slate-600 mb-3">
                        <div className="flex items-center gap-2">
                            <span>📞</span>
                            <a href={`tel:${counterpart.phone}`} className="hover:text-brand-navy">
                                {counterpart.phone ? counterpart.phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-****-$3') : '—'}
                            </a>
                        </div>
                        <div className="flex items-center gap-2">
                            <span>✉️</span>
                            <a href={`mailto:${counterpart.email}`} className="hover:text-brand-navy">
                                {counterpart.email ? counterpart.email.replace(/(.{2}).*(@.*)/, '$1***$2') : '—'}
                            </a>
                        </div>
                    </div>

                    {/* 액션 버튼 */}
                    <div className="flex gap-2">
                        {canViewCounterpartDetail && (
                            <button
                                className="flex-1 px-3 py-2 border rounded text-sm hover:bg-slate-50"
                                onClick={() => {
                                    setSelectedCounterpart(counterpart)
                                    setNoteDraft(userNotes[counterpart.id] || '')
                                    setDetailEditMode(false)
                                    setDetailEditForm({ name: '', phoneNumber: '' })
                                    setShowDetailModal(true)
                                }}
                            >
                                상세 정보
                            </button>
                        )}
                        {isGaUser && counterpart.assignmentEditable && (
                            <button
                                className="px-3 py-2 border rounded text-sm hover:bg-slate-50 disabled:opacity-50"
                                disabled={savingAssignment.has(counterpart.id) || !counterpart.gaAssigned}
                                onClick={() => toggleGaAssignment(counterpart)}
                                title={counterpart.gaAssigned ? '배정 해제' : '배정된 상태에서만 해제할 수 있습니다'}
                            >
                                배정해제
                            </button>
                        )}
                    </div>

                    {/* GA 전용 메모 */}
                    {userNotes[counterpart.id] && (
                        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                            <div className="font-medium text-yellow-800 mb-1">GA 전용 메모</div>
                            <div className="text-yellow-700">{userNotes[counterpart.id]}</div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    )

    const listTable = (
        <div className="mt-6 overflow-x-auto">
            <table className="min-w-[900px] w-full text-sm">
                <thead>
                    <tr className="text-left border-b bg-slate-50">
                        {isGaUser && <th className="px-3 py-2">보험사</th>}
                        <th className="px-3 py-2">소속</th>
                        <th className="px-3 py-2">이름</th>
                        <th className="px-3 py-2">직책</th>
                        <th className="px-3 py-2">상태</th>
                        <th className="px-3 py-2">연락처</th>
                        <th className="px-3 py-2">이메일</th>
                        <th className="px-3 py-2">액션</th>
                    </tr>
                </thead>
                <tbody>
                    {visibleCounterparts.length === 0 ? (
                        <tr><td className="px-3 py-4 text-slate-500" colSpan={isGaUser ? 8 : 7}>표시할 항목이 없습니다.</td></tr>
                    ) : (
                        visibleCounterparts.map(cp => (
                            <tr key={cp.id} className="border-b hover:bg-slate-50">
                                {isGaUser && (
                                    <td className="px-3 py-2">
                                        {getInsurerDisplayName(cp.orgId, cp.orgName)}
                                    </td>
                                )}
                                <td className="px-3 py-2">{cp.orgName || '-'}</td>
                                <td className="px-3 py-2 font-medium">{cp.name || '-'}</td>
                                <td className="px-3 py-2">{cp.position || '-'}</td>
                                <td className="px-3 py-2">
                                    <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(cp.status)}`}>
                                        {AvailabilityLabel[cp.status] || cp.status || '-'}
                                    </span>
                                </td>
                                <td className="px-3 py-2">{cp.phone || '-'}</td>
                                <td className="px-3 py-2">{cp.email || '-'}</td>
                                <td className="px-3 py-2">
                                    <div className="flex gap-2">
                                        {canViewCounterpartDetail && (
                                            <button
                                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                                                onClick={() => {
                                                    setSelectedCounterpart(cp)
                                                    setNoteDraft(userNotes[cp.id] || '')
                                                    setDetailEditMode(false)
                                                    setDetailEditForm({ name: '', phoneNumber: '' })
                                                    setShowDetailModal(true)
                                                }}
                                            >
                                                상세정보
                                            </button>
                                        )}
                                        {isGaUser && cp.assignmentEditable && (
                                            <button
                                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                                                disabled={savingAssignment.has(cp.id) || !cp.gaAssigned}
                                                onClick={() => toggleGaAssignment(cp)}
                                                title={cp.gaAssigned ? '배정 해제' : '배정된 상태에서만 해제할 수 있습니다'}
                                            >
                                                배정해제
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    )

    const downloadVisibleAsCsv = () => {
        const header = {
            ...(isGaUser ? { 보험사: '보험사' } : {}),
            소속: '소속',
            이름: '이름',
            직책: '직책',
            상태: '상태',
            연락처: '연락처',
            이메일: '이메일',
        }
        const rows = visibleCounterparts.map(cp => ({
            ...(isGaUser ? { 보험사: getInsurerDisplayName(cp.orgId, cp.orgName) } : {}),
            소속: cp.orgName || '',
            이름: cp.name || '',
            직책: cp.position || '',
            상태: AvailabilityLabel[cp.status] || cp.status || '',
            연락처: cp.phone || '',
            이메일: cp.email || '',
        }))
        const kind = isGaUser ? 'insurer_contacts' : (isInsurerUser ? 'ga_users' : 'partners')
        const suffix = gaViewMode === 'ALL_LIST' ? 'all' : 'filtered'
        downloadCsv(`people_${kind}_${suffix}_${formatDateYmd()}.csv`, [header, ...rows])
    }

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">사용자/담당자</h1>
                    <div className="mt-2">
                        <PeopleTabs />
                    </div>
                    <p className="text-slate-600 mt-3">{secondLabel} · {subtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                    {isGaUser && gaViewMode !== 'ALL_LIST' && (
                        <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setGaViewMode('ALL_LIST')}>
                            전체 원수사 보기
                        </button>
                    )}
                    {isGaUser && gaViewMode === 'ALL_LIST' && (
                        <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setGaViewMode('CARDS')}>
                            카드로 보기
                        </button>
                    )}
                    <button type="button" className="hidden md:inline-flex rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={downloadVisibleAsCsv}>
                        CSV 다운로드
                    </button>
                </div>
            </div>

            {/* 검색 및 필터 */}
            <div className="mt-6 space-y-4">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex-1 min-w-64">
                        <input
                            className="w-full rounded border px-3 py-2 text-sm"
                            placeholder="검색: 이름, 직책, 지점, 원수사명"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <select className="rounded border px-3 py-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="ALL">상태 전체</option>
                        <option value="ON_DUTY">업무중</option>
                        <option value="TRAINING">교육중</option>
                        <option value="VACATION">휴가중</option>
                        <option value="AWAY">부재중</option>
                    </select>
                    {!isGaUser && (
                        <select className="rounded border px-3 py-2 text-sm" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
                            <option value="ALL">전체</option>
                            {orgOptions.map(org => (
                                <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                        </select>
                    )}
                    <select className="rounded border px-3 py-2 text-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="responseTime">응답속도 빠른 순</option>
                        <option value="recentActivity">최근 활동 순</option>
                        <option value="completionCount">처리건수 많은 순</option>
                        <option value="name">이름 순</option>
                    </select>
                </div>

                {/* GA: 생명/손해 선택 + 원수사 탭 */}
                {isGaUser && gaViewMode !== 'ALL_LIST' && (
                    <div className="space-y-3 pb-2">
                        <div className="inline-flex rounded border bg-white overflow-hidden text-sm">
                            <button
                                type="button"
                                className={`px-4 py-2 border-r ${insurerCategory === 'ALL' ? 'bg-brand-navy text-white' : 'bg-white text-slate-700'}`}
                                onClick={() => setInsurerCategory('ALL')}
                            >
                                전체
                            </button>
                            <button
                                type="button"
                                className={`px-4 py-2 border-r ${insurerCategory === 'NONLIFE' ? 'bg-brand-navy text-white' : 'bg-white text-slate-700'}`}
                                onClick={() => setInsurerCategory('NONLIFE')}
                            >
                                손해보험
                            </button>
                            <button
                                type="button"
                                className={`px-4 py-2 ${insurerCategory === 'LIFE' ? 'bg-brand-navy text-white' : 'bg-white text-slate-700'}`}
                                onClick={() => setInsurerCategory('LIFE')}
                            >
                                생명보험
                            </button>
                        </div>

                        {/* 원수사별 탭 + 공지 보기 버튼 */}
                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                            {/* PC: 기존 탭 유지 */}
                            <div className="hidden md:flex flex-wrap gap-2">
                                {(insurerCategory === 'LIFE'
                                    ? lifeInsurers
                                    : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
                                ).map(ins => (
                                    <button
                                        key={ins.id}
                                        type="button"
                                        className={`px-3 py-1 rounded-full border ${selectedInsurerId === ins.id ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-700 border-slate-300'}`}
                                        onClick={() => {
                                            setSelectedInsurerId(ins.id)
                                            // 탭 선택 시 배정 여부를 즉시 확인(미확인 상태 최소화)
                                            ensureAssignedForCompany(ins.id)
                                        }}
                                    >
                                        {getInsurerDisplayName(ins.id, ins.name)}
                                        {(() => {
                                            const key = String(ins.id)
                                            if (checkingAssignedCompanyIds.has(key)) {
                                                return (
                                                    <span className="ml-2 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px]">
                                                        확인중
                                                    </span>
                                                )
                                            }
                                            if (assignedByCompanyId[key] === false) {
                                                return (
                                                    <span className="ml-2 rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 text-[11px]">
                                                        미배정
                                                    </span>
                                                )
                                            }
                                            return null
                                        })()}
                                    </button>
                                ))}
                                {(insurerCategory === 'LIFE'
                                    ? lifeInsurers
                                    : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
                                ).length === 0 && (
                                    <span className="text-xs text-slate-500">매핑된 원수사가 없습니다.</span>
                                )}
                            </div>

                            {/* Mobile: 원수사 목록을 셀렉트로 */}
                            <div className="w-full md:hidden">
                                {(() => {
                                    const list = (insurerCategory === 'LIFE'
                                        ? lifeInsurers
                                        : (insurerCategory === 'NONLIFE' ? nonLifeInsurers : insurerCompanies)
                                    )
                                    if (!Array.isArray(list) || list.length === 0) {
                                        return <div className="text-xs text-slate-500">매핑된 원수사가 없습니다.</div>
                                    }
                                    return (
                                        <select
                                            className="w-full rounded border px-3 py-2 text-sm"
                                            value={selectedInsurerId != null ? String(selectedInsurerId) : ''}
                                            onChange={(e) => {
                                                const v = e.target.value
                                                if (!v) return
                                                const idNum = Number(v)
                                                setSelectedInsurerId(idNum)
                                                ensureAssignedForCompany(idNum)
                                            }}
                                        >
                                            {list.map((ins) => {
                                                const key = String(ins.id)
                                                const baseName = getInsurerDisplayName(ins.id, ins.name)
                                                const suffix = checkingAssignedCompanyIds.has(key)
                                                    ? ' (확인중)'
                                                    : (assignedByCompanyId[key] === false ? ' (미배정)' : '')
                                                return (
                                                    <option key={String(ins.id)} value={String(ins.id)}>
                                                        {baseName}{suffix}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                    )
                                })()}
                            </div>
                            {selectedInsurerId && (
                                <div className="flex flex-col md:flex-row md:items-center gap-2">
                                    {assignedByCompanyId[String(selectedInsurerId)] === false && (
                                        <div className="w-full md:w-auto text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
                                            담당자가 배정되어 있지 않습니다. 담당자를 생성/배정해주세요.
                                        </div>
                                    )}

                                    {/* 담당자 배정 모드 + 전환 */}
                                    {canManageAssignments && (
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className="text-slate-500 whitespace-nowrap">담당자 배정</span>
                                            <span className={`font-medium ${selectedManagerAssignmentMode === 'GA_MANAGED' ? 'text-emerald-700' : 'text-slate-700'}`}>
                                                {selectedManagerAssignmentMode === 'GA_MANAGED' ? 'GA 관리' : '원수사 관리'}
                                            </span>
                                            <button
                                                type="button"
                                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                                                disabled={savingMode || !selectedInsurerMappingOrgId}
                                                onClick={() => {
                                                    if (!selectedInsurerMappingOrgId) {
                                                        show('매핑된 원수사가 없어 전환할 수 없습니다. 먼저 원수사- GA 조직 매핑을 추가하세요.', 'error')
                                                        return
                                                    }
                                                    updateManagerAssignmentMode(
                                                        selectedManagerAssignmentMode === 'GA_MANAGED' ? 'INSURER_MANAGED' : 'GA_MANAGED',
                                                        selectedInsurerMappingOrgId
                                                    )
                                                }}
                                                title="해당 원수사의 담당자 배정 주체를 변경합니다"
                                            >
                                                전환
                                            </button>
                                        </div>
                                    )}

                                    {/* 액션 버튼: 모바일은 2열 그리드, PC는 기존 inline */}
                                    <div className="grid grid-cols-2 gap-2 md:flex md:items-center md:gap-2">
                                        {canManageAssignments && (
                                            <button
                                                type="button"
                                                className="w-full md:w-auto px-3 py-2 md:px-3 md:py-1 rounded border text-xs hover:bg-slate-50 whitespace-nowrap"
                                                onClick={() => setCreateInsurerUserOpen(true)}
                                            >
                                                담당자 생성
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="w-full md:w-auto px-3 py-2 md:px-3 md:py-1 rounded border text-xs hover:bg-slate-50 whitespace-nowrap"
                                            onClick={() => {
                                                const params = new URLSearchParams()
                                                params.set('targetOrgIds', String(selectedInsurerMappingOrgId || selectedInsurerId))
                                                navigate(`/notices?${params.toString()}`)
                                            }}
                                        >
                                            공지 보기
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* GA: 선택된 원수사(회사) 탭에 담당자 정보가 없을 때 안내 */}
            {isGaUser && gaViewMode !== 'ALL_LIST' && selectedInsurerId && visibleCounterparts.length === 0 && (
                <div className="mt-3 rounded border bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    담당자가 배정되어 있지 않습니다. 담당자를 생성/배정해주세요.
                </div>
            )}

            <div className="mt-1">
                <ListContainer
                    loading={loading}
                    error={error}
                    onRetry={() => reload()}
                    header={null}
                    table={(isInsurerUser || gaViewMode === 'ALL_LIST') ? listTable : cards}
                    footer={null}
                />
            </div>

            {createInsurerUserOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setCreateInsurerUserOpen(false)}>
                    <form className="w-full max-w-md rounded bg-white p-6 space-y-4" onClick={(e) => e.stopPropagation()} onSubmit={createInsurerUser}>
                        <h3 className="text-lg font-semibold">원수사 담당자 생성</h3>
                        <p className="text-xs text-slate-500">
                            선택된 원수사에 담당자를 생성합니다. (원수사 관리자가 서비스를 사용 중이면 생성할 수 없습니다.)
                        </p>
                        <div>
                            <label className="block text-sm">이름</label>
                            <input className="mt-1 w-full rounded border px-3 py-2" value={createInsurerUserForm.name} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, name: e.target.value }))} required />
                        </div>
                        <div>
                            <label className="block text-sm">이메일 (선택)</label>
                            <input type="email" className="mt-1 w-full rounded border px-3 py-2" value={createInsurerUserForm.email} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, email: e.target.value }))} placeholder="비워두면 더미 이메일이 자동 생성됩니다" />
                        </div>
                        {!Boolean(createInsurerUserForm.email && String(createInsurerUserForm.email).trim()) && (
                            <div>
                                <label className="block text-sm">배정 지사</label>
                                <select
                                    className="mt-1 w-full rounded border px-3 py-2"
                                    value={createInsurerUserForm.gaBranchOrgId || ''}
                                    onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, gaBranchOrgId: e.target.value }))}
                                    required
                                >
                                    {gaBranchOptions.length === 0 ? (
                                        <option value={String(user?.orgId || '')}>내 조직</option>
                                    ) : (
                                        gaBranchOptions.map(o => (
                                            <option key={String(o.id)} value={String(o.id)}>
                                                {o.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                                <p className="text-xs text-slate-500 mt-1">
                                    사업단 계정으로 생성하는 경우, 여기서 선택한 지사에 담당자가 배정됩니다.
                                </p>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm">전화번호</label>
                            <input className="mt-1 w-full rounded border px-3 py-2" value={createInsurerUserForm.phoneNumber} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, phoneNumber: e.target.value }))} required />
                        </div>
                        <div>
                            <label className="block text-sm">역할</label>
                            <select className="mt-1 w-full rounded border px-3 py-2" value={createInsurerUserForm.role} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, role: e.target.value }))}>
                                <option value="INSURER_MANAGER">원수사 관리자</option>
                                <option value="INSURER_EDUCATION_MANAGER">교육 매니저</option>
                                <option value="INSURER_PLAN_MANAGER">설계 매니저</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm">임시 비밀번호 (이메일 직접 입력 시에만 사용)</label>
                            <div className="mt-1 flex gap-2">
                                <input type="text" className="flex-1 rounded border px-3 py-2" value={createInsurerUserForm.password} onChange={(e) => setCreateInsurerUserForm(prev => ({ ...prev, password: e.target.value }))} />
                                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setCreateInsurerUserForm(prev => ({ ...prev, password: generatePassword() }))}>
                                    재생성
                                </button>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">더미 이메일 자동 생성 모드에서는 로그인 목적이 아니므로 비밀번호는 저장되지 않습니다.</p>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setCreateInsurerUserOpen(false)}>취소</button>
                            <button type="submit" className="rounded bg-brand-navy text-white px-3 py-2 text-sm">생성</button>
                        </div>
                    </form>
                </div>
            )}

            {/* 상세 모달 */}
            {showDetailModal && selectedCounterpart && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowDetailModal(false)}>
                    <div className="w-full max-w-2xl rounded bg-white p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">{selectedCounterpart.name} 상세 정보</h3>
                            <button className="text-slate-500 hover:text-slate-700" onClick={() => setShowDetailModal(false)}>✕</button>
                        </div>

                        <div className="space-y-4 text-sm">
                            {/* 기본 정보 */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-slate-600 font-medium">이름</label>
                                    <div className="mt-1">
                                        {canEditGaProxyCounterpart && detailEditMode ? (
                                            <input
                                                className="w-full rounded border px-3 py-2"
                                                value={detailEditForm.name}
                                                onChange={(e) => setDetailEditForm(prev => ({ ...prev, name: e.target.value }))}
                                                disabled={detailSaving}
                                            />
                                        ) : (
                                            selectedCounterpart.name
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-600 font-medium">직책</label>
                                    <div className="mt-1">{selectedCounterpart.position}</div>
                                </div>
                                <div>
                                    <label className="block text-slate-600 font-medium">소속</label>
                                    <div className="mt-1">{selectedCounterpart.orgName}</div>
                                </div>
                                <div>
                                    <label className="block text-slate-600 font-medium">담당 지사</label>
                                    <div className="mt-1">
                                        {canEditGaProxyCounterpart && detailEditMode ? (
                                            <div className="space-y-2">
                                                {/* 현재 담당 지사 목록 */}
                                                <div className="flex flex-wrap gap-2">
                                                    {(selectedGaBranches || []).length > 0 ? (
                                                        (selectedGaBranches || []).map((m) => (
                                                            <span
                                                                key={`${selectedCounterpart.id}-${String(m.gaBranchOrgId)}`}
                                                                className="inline-flex items-center gap-2 rounded border bg-slate-50 px-2 py-1 text-xs"
                                                            >
                                                                <span>{m.gaBranchOrgName || m.gaBranchOrgId}</span>
                                                                <button
                                                                    type="button"
                                                                    className="text-slate-500 hover:text-red-600 disabled:opacity-50"
                                                                    disabled={detailSaving}
                                                                    onClick={() => removeGaBranchMappingForSelected(m.gaBranchOrgId, m.gaBranchOrgName)}
                                                                    title="담당 지사 배정 해제"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="text-slate-500 text-xs">현재 배정된 담당 지사가 없습니다.</span>
                                                    )}
                                                </div>

                                                {/* 추가 */}
                                                <div className="flex gap-2">
                                                    <select
                                                        className="flex-1 rounded border px-3 py-2"
                                                        value={detailEditForm.gaBranchOrgId || ''}
                                                        onChange={(e) => setDetailEditForm(prev => ({ ...prev, gaBranchOrgId: e.target.value }))}
                                                        disabled={detailSaving}
                                                    >
                                                        <option value="">추가할 지사를 선택하세요</option>
                                                        {(gaBranchOptions || []).map(o => (
                                                            <option key={String(o.id)} value={String(o.id)}>
                                                                {o.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                                                        disabled={detailSaving || !detailEditForm.gaBranchOrgId}
                                                        onClick={addGaBranchMappingForSelected}
                                                    >
                                                        추가
                                                    </button>
                                                </div>
                                                <p className="text-xs text-slate-500">
                                                    대표 없이 복수 담당 지사를 관리합니다. (추가/해제는 즉시 반영)
                                                </p>
                                            </div>
                                        ) : (
                                            <span>
                                                {(() => {
                                                    // 상세 모달에서는 서버에서 /users/{id}/ga-branches로 로드한 목록이 가장 정확하다.
                                                    const fromMappings = (selectedGaBranches || [])
                                                        .map(x => (x?.gaBranchOrgName || '').toString().trim())
                                                        .filter(Boolean)
                                                    if (fromMappings.length > 0) return fromMappings.join(', ')
                                                    const names = Array.isArray(selectedCounterpart?.gaBranchOrgNames)
                                                        ? selectedCounterpart.gaBranchOrgNames.filter(Boolean)
                                                        : []
                                                    if (names.length > 0) return names.join(', ')
                                                    const fromList = selectedCounterpart?.gaBranchOrgName
                                                    return fromList || '-'
                                                })()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-600 font-medium">상태</label>
                                    <div className="mt-1">
                                        <span className={`px-2 py-1 rounded text-xs ${getStatusStyle(selectedCounterpart.status)}`}>{AvailabilityLabel[selectedCounterpart.status] || selectedCounterpart.status}</span>
                                    </div>
                                </div>
                            </div>

                            {/* 연락 정보 */}
                            <div>
                                <label className="block text-slate-600 font-medium">연락 정보</label>
                                <div className="mt-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <span>📞</span>
                                        {canEditGaProxyCounterpart && detailEditMode ? (
                                            <input
                                                className="flex-1 rounded border px-3 py-2"
                                                value={detailEditForm.phoneNumber}
                                                onChange={(e) => setDetailEditForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                                disabled={detailSaving}
                                            />
                                        ) : (
                                            <span>{selectedCounterpart.phone || '—'}</span>
                                        )}
                                    </div>
                                    <div>✉️ {selectedCounterpart.email || '—'}</div>
                                </div>
                                {canEditGaProxyCounterpart && (
                                    <p className="text-xs text-slate-500 mt-2">
                                        이 담당자는 GA에서 대리 생성한 계정입니다. 수정/삭제는 여기서 진행하는 것을 권장합니다.
                                    </p>
                                )}
                            </div>

                            {/* KPI 정보 */}
                            <div>
                                <label className="block text-slate-600 font-medium">KPI (최근 90일)</label>
                                <div className="mt-1 grid grid-cols-2 gap-4">
                                    <div>
                                        <span className="text-slate-600">평균 응답시간:</span>
                                        <span className="ml-2 font-medium">{selectedCounterpart.avgResponseTime || '—'}시간</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-600">평균 완료 리드타임:</span>
                                        <span className="ml-2 font-medium">{selectedCounterpart.avgLeadTime || '—'}일</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-600">처리 건수:</span>
                                        <span className="ml-2 font-medium">{selectedCounterpart.completionCount || 0}건</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-600">보완 요청률:</span>
                                        <span className="ml-2 font-medium">{selectedCounterpart.revisionRate || '—'}%</span>
                                    </div>
                                </div>
                            </div>

                            {/* GA 전용 메모 */}
                            <div>
                                <label className="block text-slate-600 font-medium">GA 전용 메모</label>
                                <div className="mt-1">
                                    <textarea
                                        className="w-full rounded border px-3 py-2"
                                        rows={3}
                                        placeholder="이 담당자에 대한 메모를 입력하세요"
                                        value={noteDraft}
                                        onChange={(e) => setNoteDraft(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-2 justify-end">
                            {canEditGaProxyCounterpart && !detailEditMode && (
                                <>
                                    <button
                                        className="rounded border px-3 py-2 text-sm disabled:opacity-50"
                                        disabled={loadingSelectedGaBranches}
                                        onClick={() => {
                                            if (loadingSelectedGaBranches) {
                                                show('담당 지사 정보를 불러오는 중입니다. 잠시만 기다려주세요.', 'error')
                                                return
                                            }
                                            const primary = selectedGaBranches?.[0] || null
                                            setDetailEditForm({
                                                name: selectedCounterpart.name || '',
                                                phoneNumber: selectedCounterpart.phone || '',
                                                // "추가"용 선택값은 기본 비움(사용자가 선택)
                                                gaBranchOrgId: '',
                                            })
                                            setDetailEditMode(true)
                                        }}
                                    >
                                        정보 수정
                                    </button>
                                    <button
                                        className="rounded border border-red-300 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                        onClick={async () => {
                                            if (!selectedCounterpart) return
                                            const ok = window.confirm('이 담당자를 삭제(비활성화)하시겠습니까?\n\n삭제(비활성화) 후에는 목록에서 제외되며, 필요 시 다시 활성화할 수 있습니다.')
                                            if (!ok) return
                                            setDetailSaving(true)
                                            try {
                                                await api.patch(`/users/${selectedCounterpart.id}/deactivate`, { reason: 'GA 대리 생성 담당자 삭제' })
                                                show('삭제(비활성화) 완료', 'success')
                                                setShowDetailModal(false)
                                                setSelectedCounterpart(null)
                                                await reload()
                                            } catch (e) {
                                                show('삭제 실패', 'error')
                                            } finally {
                                                setDetailSaving(false)
                                            }
                                        }}
                                    >
                                        삭제
                                    </button>
                                </>
                            )}
                            {canEditGaProxyCounterpart && detailEditMode && (
                                <>
                                    <button
                                        className="rounded border px-3 py-2 text-sm"
                                        disabled={detailSaving}
                                        onClick={() => {
                                            setDetailEditMode(false)
                                            setDetailEditForm({ name: '', phoneNumber: '', gaBranchOrgId: '' })
                                        }}
                                    >
                                        취소
                                    </button>
                                    <button
                                        className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-50"
                                        disabled={detailSaving}
                                        onClick={async () => {
                                            if (!selectedCounterpart) return
                                            const name = String(detailEditForm.name || '').trim()
                                            const phoneNumber = String(detailEditForm.phoneNumber || '').trim()
                                            if (!name) return show('이름을 입력해주세요', 'error')
                                            if (!phoneNumber) return show('전화번호를 입력해주세요', 'error')
                                            setDetailSaving(true)
                                            try {
                                                // 1) 이름/전화는 별도로 저장 (여기가 성공해도 아래 지사 변경이 실패할 수 있음)
                                                await api.patch(`/users/${selectedCounterpart.id}`, { name, phoneNumber })
                                                setSelectedCounterpart(prev => prev ? ({ ...prev, name, phone: phoneNumber }) : prev)

                                                show('수정 완료', 'success')
                                                setDetailEditMode(false)
                                                await reload()
                                            } catch (e) {
                                                show('이름/전화 수정에 실패했습니다', 'error')
                                            } finally {
                                                setDetailSaving(false)
                                            }
                                        }}
                                    >
                                        저장
                                    </button>
                                </>
                            )}
                            <button
                                className="rounded border px-3 py-2 text-sm"
                                onClick={async () => {
                                    if (!selectedCounterpart) return
                                    try {
                                        await saveNote(selectedCounterpart.id, noteDraft)
                                    } catch {
                                        // saveNote 내부에서 토스트 처리
                                    }
                                }}
                            >
                                메모 저장
                            </button>
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setShowDetailModal(false)}>닫기</button>
                            {/* 설계 의뢰는 설계관리 메뉴로 일원화(여기서는 숨김) */}
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}
